import React, { useEffect, useState } from "react";
import Container from "../../../../components/commonContainer/Container";
import AddIcon from "@mui/icons-material/Add";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import companyApiService from "../../../services/companyApiService";
import CommonModal from "../../../../components/commonModal/CommonModal";
import DeleteDialog from "../../../../components/delete/DeleteDialog";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../utils/toast/toast";
import PlanForm, { Plan } from "./PlanForm";
import "./SubscriptionPlans.scss";
import "../../../../components/headers/addbutton.scss";
import useSubmitting from "../../../../hooks/useSubmitting";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [deleteId, setDeleteId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const [submitting, wrap] = useSubmitting();

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await companyApiService.getPlans();
      setPlans(response.data.plans || []);
    } catch (err) {
      console.error("Failed to fetch plans:", err);
      showErrorToast("Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (plan?: Plan) => {
    setSelectedPlan(plan || null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPlan(null);
  };

  const handleFormSubmit = async (planData: any) => {
    console.log(planData);

    setLoading(true);
    try {
      if (selectedPlan) {
        await companyApiService.updatePlan(
          selectedPlan.id.toString(),
          planData,
        );
        showSuccessToast("Plan updated successfully");
      } else {
        console.log("hera");

        await companyApiService.createPlan(planData);
        showSuccessToast("Plan created successfully");
      }

      handleClose();
      fetchPlans();
    } catch (err: any) {
      console.error("Failed to save plan:", err);
      showErrorToast(err.response?.data?.message || "Failed to save plan");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id.toString());
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await companyApiService.deletePlan(deleteId);
      showSuccessToast("Plan deactivated successfully");
      fetchPlans();
      setDeleteOpen(false);
      setDeleteId("");
    } catch (err: any) {
      console.error("Failed to delete plan:", err);
      showErrorToast(err.response?.data?.message || "Failed to delete plan");
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(query.trim() !== "");
  };

  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
  };

  const getBillingDisplay = (plan: Plan) => {
    if (plan.billingType === "custom" && plan.customDays) {
      return `${plan.customDays} days`;
    }
    return plan.billingType;
  };

  const filteredPlans = isSearching
    ? plans.filter(
        (plan) =>
          plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          plan.currency.toLowerCase().includes(searchQuery.toLowerCase()) ||
          plan.billingType.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : plans;

  return (
    <Container className="product">
      {/* Header with Title, Search, and Add Button */}
      <div className="addbutton">
        <h2>Subscription Plans</h2>

        <div className="search_input_container">
          <div className="form__group search_group">
            <div className="search_input_wrapper">
              <SearchIcon className="search_icon" />
              <input
                type="text"
                className="form__field"
                placeholder="Search plans by name, currency, billing type..."
                value={searchQuery}
                onChange={handleSearch}
                disabled={loading}
                style={{ paddingLeft: "44px" }}
              />
              {searchQuery && (
                <button className="clear_search_btn" onClick={clearSearch}>
                  <ClearIcon />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="action_buttons">
          <button className="add" onClick={() => handleOpen()}>
            <AddIcon />
            Add Plan
          </button>
        </div>
      </div>

      {/* Search Results Info */}
      {isSearching && (
        <div className="search_results_info">
          <span className="results_count">{filteredPlans.length}</span>
          {filteredPlans.length === 1 ? " result" : " results"} found for "
          {searchQuery}"
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading">Loading plans...</div>
      ) : filteredPlans.length === 0 ? (
        <div className="no_search_results">
          <SearchIcon className="empty_icon" />
          <h3>No plans found</h3>
          <p>
            {isSearching
              ? "Try adjusting your search terms"
              : "Click 'Add Plan' to create your first subscription plan"}
          </p>
        </div>
      ) : (
        <table className="employees-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Plan Name</th>
              <th>Amount</th>
              <th>Currency</th>
              <th>Billing Type</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPlans.map((plan, index) => (
              <tr key={plan.id}>
                <td>{index + 1}</td>
                <td className="plan-name">{plan.name}</td>
                <td className="plan-amount">{plan?.amount}</td>
                <td>{plan.currency}</td>
                <td className="plan-billing">{getBillingDisplay(plan)}</td>
                <td className="plan-description">{plan.description || "—"}</td>
                <td>
                  {plan.isActive ? (
                    <span className="status-badge active">
                      <CheckCircleIcon /> Active
                    </span>
                  ) : (
                    <span className="status-badge inactive">
                      <CancelIcon /> Inactive
                    </span>
                  )}
                </td>
                <td>
                  <button onClick={() => handleOpen(plan)} className="edit">
                    <BorderColorIcon />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(plan.id)}
                    className="delete"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Plan Modal */}
      <CommonModal
        open={open}
        setopen={handleClose}
        title={selectedPlan ? "Edit Plan" : "Create New Plan"}
      >
          <PlanForm
            selectedPlan={selectedPlan}
            onSubmit={wrap(handleFormSubmit)}
            onCancel={handleClose}
            loading={submitting || loading}
          />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteOpen}
        setopen={setDeleteOpen}
        onDelete={handleDeleteConfirm}
      />
    </Container>
  );
};

export default SubscriptionPlans;
